import React from 'react';
import dayjs from 'dayjs';
import {
  CheckCircle2, Download, FilePen, FileSignature, FileText, Mail, MailWarning, Send, ShieldCheck, Upload, XCircle,
} from 'lucide-react';
import { ActivityItem } from './api';

export const ACTIVITY_LABELS: Record<string, string> = {
  created: 'Submitted for approval',
  pan_verified: 'PAN verified',
  edited: 'Edited',
  resubmitted: 'Resubmitted for approval',
  rejected: 'Rejected',
  approved: 'Approved',
  pdf_generated: 'Agreement PDF generated',
  pdf_downloaded: 'Agreement PDF downloaded',
  signed_uploaded: 'Signed copy uploaded — executed',
  signed_downloaded: 'Signed copy downloaded',
  email_sent: 'Email sent',
  email_failed: 'Email failed',
};

const ICONS: Record<string, { icon: React.ElementType; cls: string }> = {
  created: { icon: Send, cls: 'text-blue-600 bg-blue-50' },
  resubmitted: { icon: Send, cls: 'text-blue-600 bg-blue-50' },
  pan_verified: { icon: ShieldCheck, cls: 'text-slate-600 bg-slate-100' },
  edited: { icon: FilePen, cls: 'text-slate-600 bg-slate-100' },
  rejected: { icon: XCircle, cls: 'text-red-600 bg-red-50' },
  approved: { icon: CheckCircle2, cls: 'text-green-600 bg-green-50' },
  pdf_generated: { icon: FileText, cls: 'text-slate-600 bg-slate-100' },
  pdf_downloaded: { icon: Download, cls: 'text-slate-600 bg-slate-100' },
  signed_uploaded: { icon: Upload, cls: 'text-green-700 bg-green-50' },
  signed_downloaded: { icon: FileSignature, cls: 'text-slate-600 bg-slate-100' },
  email_sent: { icon: Mail, cls: 'text-slate-500 bg-slate-100' },
  email_failed: { icon: MailWarning, cls: 'text-amber-700 bg-amber-50' },
};

const show = (v: string | null) => (v === null || v === '' ? <span className="italic text-muted-foreground">empty</span> : v);

// ---- the contract's activity trail, newest first ----
const ActivityTimeline: React.FC<{ items: ActivityItem[] }> = ({ items }) => {
  if (!items.length) return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  return (
    <ol className="max-w-3xl">
      {items.map((a, i) => {
        const { icon: Icon, cls } = ICONS[a.event_type] || ICONS.edited;
        const last = i === items.length - 1;
        return (
          <li key={a.id} className="relative flex gap-3 pb-5">
            {!last && <span className="absolute left-[15px] top-8 bottom-0 w-px bg-border" aria-hidden />}
            <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cls}`}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1 pt-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="text-sm">
                  <span className="font-medium">{ACTIVITY_LABELS[a.event_type] || a.event_type}</span>
                  {a.actor_ecode && <span className="text-muted-foreground"> by {a.actor_name || a.actor_ecode}</span>}
                </p>
                <time className="text-xs text-muted-foreground whitespace-nowrap">{dayjs(a.created_at).format('DD MMM YYYY, HH:mm')}</time>
              </div>
              {a.detail && (
                <p className={`mt-0.5 text-sm break-words ${a.event_type === 'rejected' ? 'text-red-700' : 'text-muted-foreground'}`}>
                  {a.event_type === 'rejected' ? `Reason: ${a.detail}` : a.detail}
                </p>
              )}
              {a.changes && a.changes.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 rounded-md border bg-muted/30 px-3 py-2 text-xs">
                  {a.changes.map((c) => (
                    <li key={c.field}>
                      <span className="font-medium">{c.label}:</span>{' '}
                      <span className="line-through text-muted-foreground">{show(c.old)}</span> → {show(c.new)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default ActivityTimeline;
